/**
 * Dev-only debug banner showing current role, route, and PWA state.
 * Gated by the "debug_banner" feature flag.
 * Sets a CSS variable --debug-banner-h so pages can offset their top.
 */
import { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";

function usePwaDebug() {
  const [swStatus, setSwStatus] = useState("checking…");
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Standalone / PWA mode?
    setIsStandalone(
      window.matchMedia("(display-mode: standalone)").matches ||
      (navigator as any).standalone === true
    );

    // Service-worker state
    if (!("serviceWorker" in navigator)) {
      setSwStatus("unsupported");
      return;
    }

    navigator.serviceWorker.getRegistration().then((reg) => {
      if (!reg) {
        setSwStatus("none");
        return;
      }
      if (reg.waiting) setSwStatus("waiting (update ready)");
      else if (reg.installing) setSwStatus("installing");
      else if (reg.active) setSwStatus("active");
      else setSwStatus("registered");

      reg.addEventListener("updatefound", () => {
        setSwStatus("updatefound");
      });
    });
  }, []);

  return { swStatus, isStandalone };
}

/** Height of the banner in px – keep in sync with the rendered element */
const BANNER_ID = "role-debug-banner";

export function RoleDebugBanner() {
  const location = useLocation();
  const { hasFlag } = useFeatureFlags();
  const { swStatus, isStandalone } = usePwaDebug();
  const ref = useRef<HTMLDivElement>(null);

  // Publish banner height as a CSS variable so pages can add top offset
  useEffect(() => {
    const el = ref.current;
    if (!el) {
      document.documentElement.style.setProperty("--debug-banner-h", "0px");
      return;
    }
    const h = el.getBoundingClientRect().height;
    document.documentElement.style.setProperty("--debug-banner-h", `${h}px`);
    return () => {
      document.documentElement.style.setProperty("--debug-banner-h", "0px");
    };
  });

  if (!hasFlag("debug_banner")) return null;

  const role = document.body.getAttribute("data-role") || "none";
  const routeGroup = location.pathname.startsWith("/child")
    ? "child"
    : location.pathname === "/login" || location.pathname === "/register"
      ? "public"
      : "parent";

  return (
    <div
      ref={ref}
      id={BANNER_ID}
      className="fixed top-0 left-0 right-0 z-[9999] flex flex-wrap items-center justify-center gap-x-3 gap-y-0.5 bg-foreground/90 text-background text-[10px] py-0.5 px-2 font-mono pointer-events-none"
    >
      <span>role: <strong>{role}</strong></span>
      <span>|</span>
      <span>group: <strong>{routeGroup}</strong></span>
      <span>|</span>
      <span>path: {location.pathname}</span>
      <span>|</span>
      <span>sw: <strong>{swStatus}</strong></span>
      <span>|</span>
      <span>pwa: <strong>{isStandalone ? "yes" : "no"}</strong></span>
      <span>|</span>
      <span>online: <strong>{navigator.onLine ? "yes" : "no"}</strong></span>
    </div>
  );
}
